import axios from "axios";
import { serverLink } from "./serverLink";

const axiosInstance = axios.create({
  baseURL: `${serverLink}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track in-flight refresh to avoid duplicate calls
let refreshPromise = null;

// Refresh outcomes. The session ends only when the refresh token can never
// work again: the server says it's dead (revoked or expired — the backend's
// REFRESH_TOKEN_INVALID code), it's malformed (400), or there is none.
// Anything else (a 429 from the rate limiter, a 5xx, no network) is
// transient: keep the session; the next request refreshes again.
export const REFRESH_OK = "ok";
export const REFRESH_DEAD = "dead";
export const REFRESH_TRANSIENT = "transient";

const DEAD_REFRESH_CODE = "REFRESH_TOKEN_INVALID";

// Runs the /auth/refresh-token call exactly once across concurrent callers
// (HTTP 401 retries AND socket reconnects) and broadcasts the new pair to
// localStorage + the Redux store.
//   ok        -> { status: REFRESH_OK, accessToken }
//   dead      -> { status: REFRESH_DEAD }
//   transient -> { status: REFRESH_TRANSIENT }
export async function refreshAccessTokenOnce() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return { status: REFRESH_DEAD };

  try {
    if (!refreshPromise) {
      refreshPromise = axios.post(`${serverLink}/api/auth/refresh-token`, {
        refreshToken,
      });
    }
    const { data } = await refreshPromise;
    refreshPromise = null;

    const newAccessToken = data?.data?.accessToken;
    const newRefreshToken = data?.data?.refreshToken;
    if (!newAccessToken) return { status: REFRESH_TRANSIENT };

    localStorage.setItem("authToken", newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
    }

    const {
      syncDashboardAdminIdFromAccessToken,
    } = require("../utils/adminSessionFromToken");
    syncDashboardAdminIdFromAccessToken(newAccessToken);

    const { sessionTokensUpdated } = require("../features/user/userSlice");
    const store = require("../store/store").default;
    store.dispatch(
      sessionTokensUpdated({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
      }),
    );

    return { status: REFRESH_OK, accessToken: newAccessToken };
  } catch (err) {
    refreshPromise = null;
    const status = err?.response?.status;
    // 400 = the stored refresh token is malformed; it can never work either.
    const dead =
      status === 400 ||
      (status === 401 && err?.response?.data?.code === DEAD_REFRESH_CODE);
    return { status: dead ? REFRESH_DEAD : REFRESH_TRANSIENT };
  }
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      error.response?.data?.code === "TOKEN_EXPIRED" &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshed = await refreshAccessTokenOnce();
      if (refreshed.status === REFRESH_DEAD) {
        forceLogout();
        return Promise.reject(error);
      }
      // Transient: this request fails, the admin stays signed in.
      if (refreshed.status !== REFRESH_OK) return Promise.reject(error);

      originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  },
);

export function forceLogout() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("adminId");
  localStorage.removeItem("adminName");
  window.location.href = "/login";
}

export default axiosInstance;
